'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import LocalizedPrice from '@/components/LocalizedPrice';
import { useRouter } from '@/i18n/navigation';
import { useCartAvailabilityChecker } from '@/lib/hooks/useCartAvailabilityChecker';
import { useCheckoutCartOffer } from '@/lib/hooks/useCheckoutCartOffer';
import {
  checkoutOfferNotice,
  displayedLinePrice,
} from '@/lib/sharedCart/checkoutCartOffer';
import { useCheckoutStore } from '@/stores/useCheckoutStore';

import { OrderItemLine } from '../../checkout/components/OrderItemLine';
import { ShippingEstimator } from './ShippingEstimator';

/** The cart-page wording of each offer notice (`offerNotice`). */
const OFFER_NOTICE_KEYS = {
  offer_dropped: 'Panier.offer_dropped_notice',
  removal_drops_offer: 'Panier.removal_drops_offer_notice',
  // Même phrase sur les deux écrans : une seule clé.
  piece_sold: 'PanierPartage.piece_sold',
} as const;

export const Cart = (): React.JSX.Element => {
  const { cartItems, loading, totalDetails } = useCheckoutStore();
  const t = useTranslations();

  /*
   * #846 — LE RETRAIT EST TOUJOURS PERMIS, SON PRIX EST ANNONCÉ.
   *
   * Le libraire n'oblige plus à prendre la sélection entière. Case « retrait
   * autorisé » décochée, retirer une pièce fait tomber l'offre : les pièces
   * restantes repassent au prix public, sans remise. L'écran le dit AVANT le
   * geste (l'offre tient encore) et APRÈS (elle est tombée), et chaque ligne
   * affiche le prix que la caisse facturera.
   */
  const offer = useCheckoutCartOffer();
  const notice = checkoutOfferNotice(cartItems, offer);

  const { checkAvailability, checking } = useCartAvailabilityChecker();
  const [redirecting, setRedirecting] = useState(false);
  const router = useRouter();

  const handleCheckoutClick = useCallback(async () => {
    checkAvailability(() => {
      setRedirecting(true);
      router.push('/checkout');
    });
  }, [router, checkAvailability]);

  return (
    <div className="">
      <h2 className="mb-6 text-3xl font-bold text-gray-900">
        {t('Panier.titre_de_la_page')}
      </h2>
      {cartItems.length === 0 && !loading.cart ? (
        <p className="text-lg">{t('Panier.message_panier_vide')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Cart Items (Left Column) */}
          <div className="lg:col-span-8">
            <div className="divide-y divide-gray-200">
              {loading.cart && (
                <div className="grid gap-4">
                  <div className="skeleton h-28 w-full" />
                </div>
              )}
              <div className="flex flex-col gap-4">
                {cartItems.map((item) => (
                  <OrderItemLine
                    link
                    target="_blank"
                    key={`oil-${item.id}`}
                    item={item}
                    unitPrice={displayedLinePrice(item, offer)}
                    canRemove
                  />
                ))}
              </div>
              {/* Zone d'annonce STABLE : seul son texte change. */}
              <p role="status" className="pt-4 text-sm text-gray-600">
                {notice === null ? null : t(OFFER_NOTICE_KEYS[notice])}
              </p>
            </div>
          </div>

          {/* Order Summary (Right Column) */}
          <div className="lg:col-span-4">
            <div className="rounded-lg bg-gray-50 p-6 shadow">
              <div className="">
                {/*
                  H17 — LA REMISE NÉGOCIÉE S'ÉVAPORAIT ENTRE L'OFFRE ET LA CAISSE.
                  Cet écran ne lisait que `totalDetails.totalPanier`, alors que
                  le store porte DÉJÀ `totalRemise` et `total` : le client
                  quittait la page de l'offre sur 581 €, lisait ici « Total du
                  panier — 606 € », et croyait son offre perdue. On affiche les
                  trois lignes que `computeTotal` a déjà calculées, comme
                  `OrderSummary` — ON NE RECALCULE RIEN À LA MAIN, sinon
                  l'écran diverge du débit.
                */}
                <div className="flex items-center justify-between text-base font-medium text-gray-900">
                  <p>{t('Panier.SummaryBloc.label_total_panier')}</p>
                  <p>
                    <LocalizedPrice
                      price={totalDetails.totalPanier}
                      vendu={false}
                      prixSurDemande={false}
                    />
                  </p>
                </div>
                {totalDetails.totalRemise ? (
                  <>
                    <div className="mt-2 flex items-center justify-between text-sm text-gray-700">
                      <p>{t('Panier.SummaryBloc.label_remise')}</p>
                      <p>
                        <LocalizedPrice
                          price={totalDetails.totalRemise}
                          vendu={false}
                          prixSurDemande={false}
                        />
                      </p>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-base font-bold text-gray-900">
                      <p>{t('Panier.SummaryBloc.label_total_a_payer')}</p>
                      <p>
                        <LocalizedPrice
                          price={totalDetails.total}
                          vendu={false}
                          prixSurDemande={false}
                        />
                      </p>
                    </div>
                  </>
                ) : null}
                {/* <CurrencyEstimator className="ml-auto w-fit" /> */}

                <div className="mt-6">
                  <button
                    disabled={checking || redirecting}
                    onClick={() => handleCheckoutClick()}
                    className={twMerge(
                      'eo-btn-primary block w-full text-center',
                      checking || redirecting ? 'eo-btn-primary-disabled' : '',
                    )}
                  >
                    {!checking ? (
                      t(
                        redirecting
                          ? 'Panier.SummaryBloc.label_bouton_checkout_redirection'
                          : 'Panier.SummaryBloc.label_bouton_checkout',
                      )
                    ) : (
                      <div className="loading loading-spinner" />
                    )}
                  </button>
                </div>

                <ShippingEstimator
                  cartItems={cartItems}
                  totalPanier={totalDetails.totalPanier}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
